import {
  Button,
  Form,
  FormInstance,
  Icons,
  Input,
  Select,
  Space,
  Table,
  Tooltip,
  TextField,
} from '@pankod/refine-antd'
import { useResource } from '@pankod/refine-core'
import React from 'react'
import { InputRef, RefSelectProps } from 'antd'

import { TKapabilitas } from '@components/DataTableParticipants/types'
import { AlterProgramParticipantsResponse } from '@resources/ops-toolkit/alter-program-participants/types'
import useTableWithMeta from 'src/hooks/useTableWithMeta'
import { TCommonError } from 'src/interfaces/common'
import { kapabilitasMapper } from '@components/DataTableParticipants/blocks/utils'
import { ParticipantItem } from '@resources/ops-toolkit/alter-program-participants/edit'

type UpdateParticipantsTableProps = {
  form?: FormInstance
  updatedParticipants: Map<string, ParticipantItem>
  handleSave: (record: ParticipantItem) => void
}

interface EditableCellProps {
  title: React.ReactNode
  editable: boolean
  children: React.ReactNode
  dataIndex: keyof ParticipantItem
  record: ParticipantItem
  handleSave: (record: ParticipantItem) => void
}

type EditableTableProps = Parameters<
  typeof Table<AlterProgramParticipantsResponse>
>[0]
type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>
interface EditableRowProps {
  index: number
}
const EditableContext = React.createContext<FormInstance<any> | null>(null)

const EditableRow: React.FC<EditableRowProps> = ({ index: _, ...props }) => {
  const [form] = Form.useForm()
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  )
}

const EditableCell: React.FC<EditableCellProps> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = React.useState(false)
  const inputRef = React.useRef<InputRef>(null)
  const selectRef = React.useRef<RefSelectProps>(null)
  const form = React.useContext(EditableContext)!

  React.useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      selectRef.current?.focus()
    }
  }, [editing])

  const toggleEdit = () => {
    setEditing(!editing)
    form.setFieldsValue({ [dataIndex]: record[dataIndex] })
  }

  const save = async (initialValue?: string) => {
    try {
      const values = await form.validateFields()
      if (initialValue !== values[dataIndex]) {
        handleSave({ ...record, ...values })
      }
      toggleEdit()
    } catch (errInfo) {
      console.error('Save failed:', errInfo)
    }
  }

  let childNode = children

  if (
    editable &&
    (record.participantType === 'non-guru' || dataIndex === 'kapabilitas')
  ) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[
          {
            required: dataIndex !== 'kapabilitas',
            message: `${title} is required.`,
          },
        ]}
      >
        {dataIndex === 'kapabilitas' ? (
          <Select
            ref={selectRef}
            options={[
              { label: 'Peserta', value: 'peserta' },
              { label: 'Asisten', value: 'asisten' },
              { label: 'Fasilitator', value: 'instruktur' },
              { label: 'Admin Kelas', value: 'admin_kelas' },
            ]}
            onChange={() => save(record.kapabilitas)}
            onBlur={() => {
              setEditing(false)
            }}
            open={editing}
          />
        ) : (
          <Input
            ref={inputRef}
            onPressEnter={() => save(record[dataIndex] as string)}
            onBlur={() => save(record[dataIndex] as string)}
            placeholder={`Masukkan ${title}`}
          />
        )}
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingRight: 24 }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    )
  } else if (
    editable &&
    record.participantType === 'guru' &&
    dataIndex === 'name'
  ) {
    childNode = (
      <Tooltip
        placement="topLeft"
        title="Nama peserta dengan tipe guru tidak dapat diubah"
      >
        <div className="pl-3 cursor-not-allowed">{children}</div>
      </Tooltip>
    )
  }

  return <td {...restProps}>{childNode}</td>
}

const UpdateParticipantsTable = ({
  form,
  handleSave,
  updatedParticipants,
}: UpdateParticipantsTableProps) => {
  const searchInput = React.useRef<InputRef>(null)
  const { id } = useResource()
  const { tableProps, tableQueryResult } = useTableWithMeta<
    AlterProgramParticipantsResponse,
    TCommonError
  >({
    dataProviderName: 'lms',
    resource: `programs/${id}/participants`,
  })

  const defaultColumns: (ColumnTypes[number] & {
    editable?: boolean
    dataIndex: string
  })[] = [
    {
      title: 'ID Peserta',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      filterSearch: true,
      width: 260,
      filterIcon: (filtered: boolean) => (
        <Icons.SearchOutlined
          style={{ color: filtered ? '#1890ff' : undefined }}
        />
      ),
      onFilter: (value, record) =>
        record['email']
          .toString()
          .toLowerCase()
          .includes((value as string).toLowerCase()),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div
          className="w-[250px]"
          style={{ padding: 8 }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Input
            ref={searchInput}
            placeholder={`Cari email peserta`}
            value={`${selectedKeys[0] || ''}`}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }}
            onPressEnter={() => {
              confirm()
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<Icons.SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Cari
            </Button>
            <Button
              onClick={() => {
                clearFilters?.()
                confirm()
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
    },
    {
      title: 'Nama Peserta',
      dataIndex: 'name',
      key: 'name',
      editable: true,
      width: 200,
    },
    {
      title: 'Kapabilitas',
      dataIndex: 'kapabilitas',
      key: 'kapabilitas',
      editable: true,
      width: 200,
      render: (value) => {
        return (
          kapabilitasMapper(value) || (
            <TextField value="Kosong" italic type="secondary" />
          )
        )
      },
    },
  ]

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col
    }
    return {
      ...col,
      onCell: (record: ParticipantItem) => ({
        record: updatedParticipants.get(record.userId) || record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    }
  })

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  }

  return (
    <Form form={form} layout="vertical" onFinish={() => {}}>
      <Table
        {...tableProps}
        loading={tableQueryResult.isFetching}
        rowKey={'userId'}
        size="small"
        dataSource={tableProps.dataSource?.map((item) => ({
          ...item,
          name: updatedParticipants.get(item.userId!)?.name || item.name,
          kapabilitas: (updatedParticipants.get(item.userId!)?.kapabilitas ||
            item.kapabilitas ||
            'peserta') as TKapabilitas,
        }))}
        components={components}
        rowClassName={() => 'editable-row'}
        // @ts-expect-error
        columns={columns}
        scroll={{ x: 'max-content', y: 400 }}
      />
    </Form>
  )
}

export default UpdateParticipantsTable
